import { pluralize } from '@/utils/pluralize.ts';

/**
 * Rzeczowniki, które ekrany zmian odmieniają przez liczebnik.
 *
 * Wszystkie w jednym miejscu, bo liczby biorą się z danych, a nie z projektu:
 * to samo słowo wypada w belce szkicu, w historii i w sekcji, i musi wszędzie
 * brzmieć tak samo. Samą regułę odmiany niesie `@/utils/pluralize.ts`.
 */

/** 1 pozycja / 3 pozycje / 5 pozycji — zwinięta sekcja, belka szkicu, historia. */
export const pluralizeItems = (count: number): string =>
  pluralize(count, 'pozycja', 'pozycje', 'pozycji');

/** Biernik: „zawiera 1 pozycję / 3 pozycje / 5 pozycji”. */
export const pluralizeItemsAccusative = (count: number): string =>
  pluralize(count, 'pozycję', 'pozycje', 'pozycji');

/** 1 zmiana / 3 zmiany / 5 zmian — kropki zmian przy wierszach rachunku. */
export const pluralizeChanges = (count: number): string =>
  pluralize(count, 'zmiana', 'zmiany', 'zmian');

/** 1 rok / 3 lata / 6 lat — wiek uczestnika. */
export const pluralizeYears = (count: number): string =>
  pluralize(count, 'rok', 'lata', 'lat');

/** 1 osoba / 2 osoby / 5 osób — podpis separatora sekcji osób. */
export const pluralizePeople = (count: number): string =>
  pluralize(count, 'osoba', 'osoby', 'osób');

/**
 * 1 uczestnik / 2 uczestników / 5 uczestników — meta nagłówka ekranu
 * rezerwacji. Obie formy mnogie brzmią tu tak samo, więc odmiana sprowadza się
 * do liczby pojedynczej kontra reszta.
 */
export const pluralizeParticipants = (count: number): string =>
  Math.abs(count) === 1 ? `${count} uczestnik` : `${count} uczestników`;

/** 1 rezygnacja / 2 rezygnacje / 5 rezygnacji — dopisek przy liczbie uczestników. */
export const pluralizeWithdrawals = (count: number): string =>
  pluralize(count, 'rezygnacja', 'rezygnacje', 'rezygnacji');

/** 1 rata / 3 raty / 5 rat — nagłówek karty „Zaplanowane raty”. */
export const pluralizeInstallments = (count: number): string =>
  pluralize(count, 'rata', 'raty', 'rat');

/** 1 opłacona / 2 opłacone / 5 opłaconych — druga część tego samego nagłówka. */
export const pluralizePaidInstallments = (count: number): string =>
  pluralize(count, 'opłacona', 'opłacone', 'opłaconych');

/** Miejscownik słowa „rata”: „1 racie”, „2 ratach” — dla zdania „w … ratach”. */
export const pluralizeInstallmentsLocative = (count: number): string =>
  count === 1 ? '1 racie' : `${count} ratach`;
